import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { counterpartyService } from '../services/counterpartyService';
import { CreateCounterpartyRequest } from '../types/counterparty';
import { useTranslation } from '../hooks/useTranslation';
import { CountrySelect } from './CountrySelect';

interface AddCounterpartyModalProps {
  companyId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  name: string;
  taxId?: string;
  vatId?: string;
  country: string;
  city: string;
  zip: string;
  address?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  comment?: string;
}

const AddCounterpartyModal: React.FC<AddCounterpartyModalProps> = ({ 
  companyId, 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, formState: { errors }, watch, setValue } = useForm<FormData>();

  const createCounterpartyMutation = useMutation({
    mutationFn: (data: CreateCounterpartyRequest) => 
      counterpartyService.createCounterparty(companyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['counterparties', companyId] });
      reset();
      onSuccess();
    },
    onError: (error) => {
      console.error('Error creating counterparty:', error);
      // Ошибка будет обработана через apiClient interceptor, который покажет toast-уведомление
      // Не показываем alert, чтобы избежать дублирования
    }
  });

  const onSubmit = (data: FormData) => {
    createCounterpartyMutation.mutate(data);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 my-auto max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">{t('counterparty.addTitle')}</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col flex-1 min-h-0">
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4 overflow-y-auto flex-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('counterparty.name')} *
              </label>
              <input
                type="text"
                {...register('name', { required: t('counterparty.nameRequired') })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('counterparty.namePlaceholder')}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('counterparty.taxId')}
              </label>
              <input
                type="text"
                {...register('taxId')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('counterparty.taxIdPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('counterparty.vatId')}
              </label>
              <input
                type="text"
                {...register('vatId')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('counterparty.vatIdPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('counterparty.country')} *
              </label>
              <CountrySelect
                value={watch('country') || ''}
                onChange={(value) => setValue('country', value, { shouldValidate: true })}
                placeholder={t('counterparty.countryPlaceholder') || 'Выберите страну...'}
                required
                error={errors.country?.message}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('counterparty.city')} *
              </label>
              <input
                type="text"
                {...register('city', { required: t('counterparty.cityRequired') || 'Город обязателен' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('counterparty.cityPlaceholder') || 'Warsaw'}
              />
              {errors.city && (
                <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('counterparty.zip') || 'Почтовый индекс'} *
              </label>
              <input
                type="text"
                {...register('zip', { required: t('counterparty.zipRequired') || 'Почтовый индекс обязателен' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('counterparty.zipPlaceholder') || '00-000'}
              />
              {errors.zip && (
                <p className="mt-1 text-sm text-red-600">{errors.zip.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('counterparty.address')}
              </label>
              <input
                type="text"
                {...register('address')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('counterparty.addressPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('counterparty.contactPerson')}
              </label>
              <input
                type="text"
                {...register('contactPerson')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('counterparty.contactPersonPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('counterparty.email')}
              </label>
              <input
                type="email"
                {...register('email')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('counterparty.emailPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('counterparty.phone')}
              </label>
              <input
                type="tel"
                {...register('phone')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('counterparty.phonePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('counterparty.comment') || 'Комментарий'}
              </label>
              <textarea
                {...register('comment')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('counterparty.commentPlaceholder') || 'Введите комментарий...'}
              />
            </div>
          </form>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={createCounterpartyMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {createCounterpartyMutation.isPending ? t('common.creating') : t('common.create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCounterpartyModal;
